import { useState, useEffect } from "react";
import { Plus, Trash2, Star } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  ReorderButtons,
} from "../../../components/FormField";

import { MediaImageField } from "../../../components/MediaPickerDialog";
import { moveItem } from "./shared";

const emptyGuide = () => ({
  title: "",
  description: "",
  type: "",
  pages: "",
  highlights: [""],
  featured: false,
  file_url: "",
});

// ─── Single Guide Card ────────────────────────────────────────────────────────
function GuideCard({ guide, idx, total, onChange, onMove, onRemove }) {
  const upd = (key, val) => onChange({ ...guide, [key]: val });

  const updHighlight = (i, val) =>
    onChange({
      ...guide,
      highlights: guide.highlights.map((h, hi) => (hi === i ? val : h)),
    });
  const addHighlight = () =>
    onChange({ ...guide, highlights: [...(guide.highlights || []), ""] });
  const removeHighlight = (i) =>
    onChange({
      ...guide,
      highlights: guide.highlights.filter((_, hi) => hi !== i),
    });

  return (
    <div
      className={`border rounded-xl mb-4 overflow-hidden ${guide.featured ? "border-accent/40" : "border-border"}`}
    >
      {/* Card header */}
      <div
        className={`flex items-center justify-between px-4 py-3 border-b ${guide.featured ? "bg-accent/5 border-accent/20" : "bg-muted border-border"}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {guide.featured && (
            <Star size={13} className="text-accent shrink-0" />
          )}
          <p className="text-xs font-semibold truncate">
            {guide.title || `Guide ${idx + 1}`}
          </p>
          {guide.featured && (
            <span className="text-xs text-accent bg-accent/10 px-2 py-0.5 rounded-full shrink-0">
              Featured
            </span>
          )}
          {guide.file_url && (
            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full shrink-0">
              File ✓
            </span>
          )}
        </div>
        <div className="flex gap-2 items-center shrink-0 ml-2">
          <ReorderButtons index={idx} total={total} onMove={onMove} />
          {total > 1 && (
            <button onClick={onRemove}>
              <Trash2 size={14} className="text-destructive" />
            </button>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className="p-4 space-y-3">
        {/* Featured toggle */}
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              onClick={() => upd("featured", !guide.featured)}
              className={`w-10 h-5 rounded-full transition-colors relative ${guide.featured ? "bg-accent" : "bg-muted-foreground/30"}`}
            >
              <div
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${guide.featured ? "translate-x-5" : "translate-x-0.5"}`}
              />
            </div>
            <span className="text-xs font-medium">
              Featured (dark card, top section)
            </span>
          </label>
        </div>

        <Field label="Title">
          <TextInput
            value={guide.title}
            onChange={(e) => upd("title", e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={guide.description}
            onChange={(e) => upd("description", e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type (e.g. Guide, Checklist)">
            <TextInput
              value={guide.type}
              placeholder="Guide"
              onChange={(e) => upd("type", e.target.value)}
            />
          </Field>
          <Field label="Pages / Format">
            <TextInput
              value={guide.pages}
              placeholder="42 pages"
              onChange={(e) => upd("pages", e.target.value)}
            />
          </Field>
        </div>

        {/* Highlights */}
        <div className="flex justify-between items-center mt-1">
          <p className="text-xs font-semibold">Highlights</p>
          <button
            onClick={addHighlight}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={12} /> Add
          </button>
        </div>
        {guide.highlights?.map((h, i) => (
          <div key={i} className="flex gap-2 items-center">
            <div className="flex-1">
              <TextInput
                value={h}
                placeholder={`Highlight ${i + 1}`}
                onChange={(e) => updHighlight(i, e.target.value)}
              />
            </div>
            {guide.highlights.length > 1 && (
              <button onClick={() => removeHighlight(i)}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}

        {/* File upload */}
        <Field label="Downloadable File">
          <MediaImageField
            label="Upload PDF / Excel / Doc"
            value={guide.file_url}
            onChange={(val) => upd("file_url", val)}
          />
        </Field>
        {guide.file_url && (
          <p className="text-xs text-green-600 flex items-center gap-1">
            ✓ File uploaded — users can download this file from the website
          </p>
        )}
        {!guide.file_url && (
          <p className="text-xs text-muted-foreground/70">
            No file uploaded yet — download button will be disabled on the
            website
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function GuidesSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Expert Resources");
  const [heroHeading, setHeroHeading] = useState("Guides &");
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState("Downloads");
  const [heroDescription, setHeroDescription] = useState("");

  /* ---------------- SECTION LABELS ---------------- */
  const [labelsActive, setLabelsActive] = useState(true);
  const [featuredHeading, setFeaturedHeading] = useState("Featured Resources");
  const [featuredDescription, setFeaturedDescription] = useState(
    "Our most popular guides for ERP planning and implementation.",
  );
  const [allHeading, setAllHeading] = useState("All Resources");

  /* ---------------- GUIDES ---------------- */
  const [guidesActive, setGuidesActive] = useState(true);
  const [guides, setGuides] = useState([emptyGuide()]);

  /* ---------------- NEWSLETTER ---------------- */
  const [newsletterActive, setNewsletterActive] = useState(true);
  const [newsletterHeading, setNewsletterHeading] = useState(
    "Get New Resources Delivered",
  );
  const [newsletterDescription, setNewsletterDescription] = useState("");
  const [newsletterButtonText, setNewsletterButtonText] = useState("Subscribe");
  const [newsletterPlaceholder, setNewsletterPlaceholder] =
    useState("Enter your email");

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState("Need Personalized Guidance?");
  const [ctaDescription, setCtaDescription] = useState("");
  const [ctaText, setCtaText] = useState("Book a Free Consultation");
  const [ctaLink, setCtaLink] = useState("/contact");

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
    }

    if (d.featured_section) {
      setFeaturedHeading(d.featured_section.heading || "");
      setFeaturedDescription(d.featured_section.description || "");
    }

    if (d.all_section) {
      setAllHeading(d.all_section.heading || "");
    }

    if (d.guides?.length) setGuides(d.guides);

    if (d.newsletter) {
      setNewsletterHeading(d.newsletter.heading || "");
      setNewsletterDescription(d.newsletter.description || "");
      setNewsletterButtonText(d.newsletter.button_text || "");
      setNewsletterPlaceholder(d.newsletter.placeholder || "");
    }

    if (d.final_cta) {
      setCtaHeading(d.final_cta.heading || "");
      setCtaDescription(d.final_cta.description || "");
      setCtaText(d.final_cta.cta_text || "");
      setCtaLink(d.final_cta.cta_link || "");
    }
  }, [initialData]);

  const buildPayload = () => ({
    hero: {
      badge_text: heroBadgeText,
      heading: heroHeading,
      heading_emphasis: heroHeadingEmphasis,
      description: heroDescription,
    },
    featured_section: {
      heading: featuredHeading,
      description: featuredDescription,
    },
    all_section: {
      heading: allHeading,
    },
    guides,
    newsletter: {
      heading: newsletterHeading,
      description: newsletterDescription,
      button_text: newsletterButtonText,
      placeholder: newsletterPlaceholder,
    },
    final_cta: {
      heading: ctaHeading,
      description: ctaDescription,
      cta_text: ctaText,
      cta_link: ctaLink,
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    featuredHeading,
    featuredDescription,
    allHeading,
    guides,
    newsletterHeading,
    newsletterDescription,
    newsletterButtonText,
    newsletterPlaceholder,
    ctaHeading,
    ctaDescription,
    ctaText,
    ctaLink,
  ]);

  const updateGuide = (i, val) =>
    setGuides((s) => s.map((x, idx) => (idx === i ? val : x)));

  const featuredCount = guides.filter((g) => g.featured).length;
  const nonFeaturedCount = guides.filter((g) => !g.featured).length;

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>
      </CollapsibleSection>

      {/* SECTION LABELS */}
      <CollapsibleSection
        title="Section Headings"
        active={labelsActive}
        onToggleActive={setLabelsActive}
      >
        <Field label="Featured Section Heading">
          <TextInput
            value={featuredHeading}
            onChange={(e) => setFeaturedHeading(e.target.value)}
          />
        </Field>
        <Field label="Featured Section Description">
          <TextArea
            value={featuredDescription}
            onChange={(e) => setFeaturedDescription(e.target.value)}
          />
        </Field>
        <Field label="All Resources Section Heading">
          <TextInput
            value={allHeading}
            onChange={(e) => setAllHeading(e.target.value)}
          />
        </Field>
      </CollapsibleSection>

      {/* GUIDES */}
      <CollapsibleSection
        title={`Guides & Downloads (${featuredCount} featured, ${nonFeaturedCount} other)`}
        active={guidesActive}
        onToggleActive={setGuidesActive}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-muted-foreground">
            Toggle "Featured" on a guide to show it in the dark featured
            section. Others appear in the grid below.
          </p>
        </div>

        {guides.map((g, i) => (
          <GuideCard
            key={i}
            guide={g}
            idx={i}
            total={guides.length}
            onChange={(val) => updateGuide(i, val)}
            onMove={(f, t) => setGuides(moveItem(guides, f, t))}
            onRemove={() => setGuides((s) => s.filter((_, idx) => idx !== i))}
          />
        ))}

        <button
          onClick={() => setGuides((s) => [...s, emptyGuide()])}
          className="w-full flex items-center justify-center gap-2 text-xs py-3 border-2 border-dashed border-border rounded-xl hover:border-accent/40 hover:text-accent transition-colors"
        >
          <Plus size={14} /> Add Guide
        </button>
      </CollapsibleSection>

      {/* NEWSLETTER */}
      <CollapsibleSection
        title="Newsletter CTA"
        active={newsletterActive}
        onToggleActive={setNewsletterActive}
      >
        <Field label="Heading">
          <TextInput
            value={newsletterHeading}
            onChange={(e) => setNewsletterHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={newsletterDescription}
            onChange={(e) => setNewsletterDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email Placeholder">
            <TextInput
              value={newsletterPlaceholder}
              placeholder="Enter your email"
              onChange={(e) => setNewsletterPlaceholder(e.target.value)}
            />
          </Field>
          <Field label="Button Text">
            <TextInput
              value={newsletterButtonText}
              placeholder="Subscribe"
              onChange={(e) => setNewsletterButtonText(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* FINAL CTA */}
      <CollapsibleSection
        title="Final CTA"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={ctaDescription}
            onChange={(e) => setCtaDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={ctaText}
              onChange={(e) => setCtaText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={ctaLink}
              onChange={(e) => setCtaLink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>
    </>
  );
}
